import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../api';

const ResetPasswordPage = () => {
  const { token } = useParams();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const { password, confirmPassword } = formData;

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
    
    // Clear general error
    if (errors.general) {
      setErrors({ ...errors, general: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Password validation
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    // Confirm password validation
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    return newErrors;
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: '', color: '' };
    
    if (password.length < 6) return { strength: 'Weak', color: 'text-error' };
    if (password.length < 10) return { strength: 'Medium', color: 'text-warning' };
    return { strength: 'Strong', color: 'text-success' };
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await api.post(`/auth/reset-password/${token}`, { password });
      setMessage('Your password has been successfully reset! You can now sign in with your new password.');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to reset password. The link may have expired.';
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(password);

  if (!token) {
    return (
      <div className="form-container">
        <div className="form-header">
          <h1 className="form-title">Invalid Reset Link</h1>
          <p className="form-subtitle">
            The password reset link is missing or invalid
          </p>
        </div>
        
        <div className="alert alert--error">
          <strong>Error:</strong> Invalid or missing reset token
        </div>
        
        <div className="text-center">
          <Link to="/forgot-password" className="btn btn--primary">
            Request New Reset Link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-header">
        <h1 className="form-title">Create New Password</h1>
        <p className="form-subtitle">
          Choose a strong password to secure your account
        </p>
      </div>

      {errors.general && (
        <div className="alert alert--error">
          <strong>Reset Failed:</strong> {errors.general}
          <div className="mt-2">
            <Link to="/forgot-password" className="link">
              Request a new reset link
            </Link>
          </div>
        </div>
      )}

      {message && (
        <div className="alert alert--success">
          <strong>Password Reset Successful!</strong> {message}
        </div>
      )}

      {!message && (
        <form onSubmit={onSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="password" className="form-label form-label--required">
              New Password
            </label>
            <div className="form-input-group">
              <span className="form-input-icon"></span>
              <input
                type="password"
                id="password"
                name="password"
                className={`form-input form-input--with-icon ${errors.password ? 'form-input--error' : ''}`}
                value={password}
                onChange={onChange}
                placeholder="Enter your new password"
                required
                autoComplete="new-password"
                disabled={loading}
              />
            </div>
            {errors.password && (
              <small className="text-error">{errors.password}</small>
            )}
            {password && (
              <small className={passwordStrength.color}>
                Password strength: {passwordStrength.strength}
              </small>
            )}
            <small className="text-muted">
              Password must be at least 6 characters long
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label form-label--required">
              Confirm New Password
            </label>
            <div className="form-input-group">
              <span className="form-input-icon">🔐</span>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className={`form-input form-input--with-icon ${errors.confirmPassword ? 'form-input--error' : ''}`}
                value={confirmPassword}
                onChange={onChange}
                placeholder="Confirm your new password"
                required
                autoComplete="new-password"
                disabled={loading}
              />
            </div>
            {errors.confirmPassword && (
              <small className="text-error">{errors.confirmPassword}</small>
            )}
            {confirmPassword && password === confirmPassword && (
              <small className="text-success">✓ Passwords match</small>
            )}
          </div>

          <button
            type="submit"
            className={`btn btn--primary btn--full ${loading ? 'btn--loading' : ''}`}
            disabled={loading || !password || !confirmPassword}
          >
            {loading ? '' : 'Reset Password'}
          </button>

          <div className="form-group mt-3 text-center">
            <Link to="/login" className="link link--muted">
              ← Back to Login
            </Link>
          </div>
        </form>
      )}

      {message && (
        <div className="text-center mt-4">
          <Link to="/login" className="btn btn--primary btn--full">
            Continue to Login
          </Link>
        </div>
      )}

      <div className="mt-4 text-center">
        <p className="text-muted text-sm">
          Your new password will be encrypted and stored securely
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
